import { useMemo } from 'react';
import { formatTimeAgo } from '../../../utils/dateUtils';
import {
  getUserFriendlyStatus,
  getStatusDescription,
  getSLAMessage,
  STATUS_COLORS,
} from '../../../types/ticketActivity';
import type { SLAStatus } from '../../../types/ticketActivity';
import type { Ticket } from '../../../types/ticket';
import { getTicketCategory, getTicketSubcategory, isChangeTicket } from '../../../types/ticket';
import './TicketStatusHeader.css';

interface TicketStatusHeaderProps {
  ticket: Ticket;
  slaStatus: SLAStatus | null;
  needsUserAction: boolean;
}

/**
 * TicketStatusHeader - Clear status display at the top of ticket view
 *
 * Shows:
 * - Status in plain language
 * - Assigned team (not individual)
 * - Last update timestamp
 * - SLA indicator
 */
export function TicketStatusHeader({
  ticket,
  slaStatus,
  needsUserAction,
}: TicketStatusHeaderProps) {
  const statusLabel = getUserFriendlyStatus(ticket.status);
  const statusDescription = getStatusDescription(ticket.status);
  const statusColors = STATUS_COLORS[ticket.status];
  const slaMessage = slaStatus ? getSLAMessage(slaStatus) : null;

  // Determine SLA visual style
  const slaClassName = useMemo(() => {
    if (!slaStatus) return '';
    switch (slaStatus) {
      case 'on_track':
        return 'ticket-status-header__sla--on-track';
      case 'at_risk':
        return 'ticket-status-header__sla--at-risk';
      case 'breached':
        return 'ticket-status-header__sla--breached';
      case 'met':
        return 'ticket-status-header__sla--met';
      default:
        return '';
    }
  }, [slaStatus]);

  return (
    <header className="ticket-status-header">
      {/* Ticket number and type */}
      <div className="ticket-status-header__top">
        <span className="ticket-status-header__number">{ticket.ticketNumber}</span>
        <span className="ticket-status-header__type">{ticket.ticketType}</span>
        <span className="ticket-status-header__category">
          {isChangeTicket(ticket)
            ? getTicketCategory(ticket)
            : `${getTicketCategory(ticket)} / ${getTicketSubcategory(ticket)}`}
        </span>
      </div>

      {/* Main content */}
      <div className="ticket-status-header__main">
        {/* Left side - Description */}
        <div className="ticket-status-header__content">
          <h1 className="ticket-status-header__title">{ticket.shortDescription}</h1>
          <p className="ticket-status-header__submitted">
            Submitted {formatTimeAgo(ticket.createdAt)}
          </p>
        </div>

        {/* Right side - Status badge */}
        <div className="ticket-status-header__status-section">
          <div
            className={`ticket-status-header__status ${needsUserAction ? 'ticket-status-header__status--action-required' : ''}`}
            style={{
              backgroundColor: statusColors.bg,
              color: statusColors.text,
              borderColor: statusColors.border,
            }}
          >
            {needsUserAction && (
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="ticket-status-header__status-icon"
              >
                <circle cx="8" cy="8" r="6" />
                <path d="M8 5v3M8 10v1" strokeLinecap="round" />
              </svg>
            )}
            {statusLabel}
          </div>
          {slaMessage && slaStatus !== 'met' && (
            <span className={`ticket-status-header__sla ${slaClassName}`}>{slaMessage}</span>
          )}
        </div>
      </div>

      {/* Status description */}
      <p className="ticket-status-header__description">{statusDescription}</p>

      {/* Action required banner */}
      {needsUserAction && (
        <div className="ticket-status-header__action-banner">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="10" cy="10" r="8" />
            <path d="M10 6v4M10 12v1" strokeLinecap="round" />
          </svg>
          <div className="ticket-status-header__action-content">
            <span className="ticket-status-header__action-title">Action required</span>
            <span className="ticket-status-header__action-text">
              Please respond below to help us resolve your request faster.
            </span>
          </div>
        </div>
      )}

      {/* Assigned team indicator */}
      <div className="ticket-status-header__meta">
        <div className="ticket-status-header__meta-item">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 8a3 3 0 100-6 3 3 0 000 6zm0 2c-3.314 0-6 1.343-6 3v1h12v-1c0-1.657-2.686-3-6-3z" />
          </svg>
          <span>Assigned to IT Support Team</span>
        </div>
        <div className="ticket-status-header__meta-item">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm-.75 4a.75.75 0 011.5 0v3.25l2.22 1.28a.75.75 0 01-.75 1.3L7.7 9.55A.75.75 0 017.25 9V5z" />
          </svg>
          <span>Last updated {formatTimeAgo(ticket.updatedAt)}</span>
        </div>
      </div>
    </header>
  );
}
